import type {
  CompatibilityEntry,
  PanelCatalogEntry,
  Server,
  TopologyPresetEntry,
} from '../api/types.ts';

/**
 * The wizard's rules, as plain functions.
 *
 * Kept out of the components so they can be tested without rendering anything,
 * and so "why is Continue disabled?" has an answer that is a function rather
 * than a tangle of conditions inside a button.
 */

export type WizardStep = 'panel' | 'shape' | 'servers' | 'options' | 'review';

export const WIZARD_STEPS: readonly WizardStep[] = [
  'panel',
  'shape',
  'servers',
  'options',
  'review',
];

export type Mode = 'easy' | 'expert';

export interface WizardState {
  readonly mode: Mode;
  readonly panelAdapterId?: string;
  readonly preset?: string;
  /** slot id -> server ids, in the order the customer placed them. */
  readonly assignments: Readonly<Record<string, readonly string[]>>;
  readonly tunnel: 'automatic' | { readonly adapterId: string };
  readonly domain: string;
  readonly requestSsl: boolean;
  readonly panelPort: string;
  readonly requireEncryptedTunnel: boolean;
}

export const INITIAL_STATE: WizardState = {
  mode: 'easy',
  assignments: {},
  tunnel: 'automatic',
  domain: '',
  requestSsl: false,
  panelPort: '',
  requireEncryptedTunnel: true,
};

/** Servers a deployment may actually use. */
export function usableServers(servers: readonly Server[]): readonly Server[] {
  // Inspected, still authorised, and able to obtain root — the three things
  // without which the planner would refuse anyway. Better to not offer the
  // server than to offer it and fail at the end of the wizard.
  return servers.filter(
    (server) =>
      server.inspected &&
      !server.authorizationRevokedAt &&
      (server.summary?.canObtainRoot ?? false),
  );
}

export function isPanelSupportedForPreset(
  compatibility: readonly CompatibilityEntry[],
  panelAdapterId: string,
  preset: string,
): boolean {
  const entry = compatibility.find(
    (row) => row.panelAdapterId === panelAdapterId && row.preset === preset,
  );
  // Unknown combinations are allowed: the planner is the authority, and the
  // catalog silently hiding a working option is the worse failure.
  return entry?.supported ?? true;
}

export function minimumServersFor(preset: TopologyPresetEntry): number {
  return preset.slots.reduce((total, slot) => total + slot.min, 0);
}

/** Every server id used anywhere in the assignment. */
export function assignedServerIds(state: WizardState): readonly string[] {
  return Object.values(state.assignments).flat();
}

export function assignServer(
  state: WizardState,
  slotId: string,
  index: number,
  serverId: string | undefined,
): WizardState {
  const current = [...(state.assignments[slotId] ?? [])];
  while (current.length <= index) current.push('');
  current[index] = serverId ?? '';
  return {
    ...state,
    // Empty strings are the holes the customer has not filled yet; they are
    // stripped on the way out, not on the way in, so removing the first of
    // three servers does not shuffle the other two.
    assignments: { ...state.assignments, [slotId]: current },
  };
}

/** The assignment as the API wants it: no holes. */
export function cleanAssignments(state: WizardState): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [slot, ids] of Object.entries(state.assignments)) {
    const filled = ids.filter((id) => id !== '');
    if (filled.length > 0) out[slot] = filled;
  }
  return out;
}

export interface StepValidity {
  readonly complete: boolean;
  /** Why not, as a message key plus values, for the interface to translate. */
  readonly reason?: { readonly key: string; readonly values?: Record<string, number> };
}

export function validateStep(
  step: WizardStep,
  state: WizardState,
  context: {
    readonly panels: readonly PanelCatalogEntry[];
    readonly presets: readonly TopologyPresetEntry[];
    readonly servers: readonly Server[];
  },
): StepValidity {
  const preset = context.presets.find((entry) => entry.preset === state.preset);

  switch (step) {
    case 'panel':
      return { complete: Boolean(state.panelAdapterId) };

    case 'shape': {
      if (!preset) return { complete: false };
      const needed = minimumServersFor(preset);
      const have = usableServers(context.servers).length;
      if (have < needed) {
        return {
          complete: false,
          reason: { key: 'wizard.shape.needsMoreServers', values: { count: needed, have } },
        };
      }
      return { complete: true };
    }

    case 'servers': {
      if (!preset) return { complete: false };
      for (const slot of preset.slots) {
        const filled = (state.assignments[slot.id] ?? []).filter((id) => id !== '');
        if (filled.length < slot.min) return { complete: false };
        if (filled.length > slot.max) return { complete: false };
      }
      const all = assignedServerIds(state).filter((id) => id !== '');
      // The same machine in two roles would mean a tunnel to itself.
      if (new Set(all).size !== all.length) return { complete: false };
      return { complete: true };
    }

    case 'options': {
      if (
        state.domain &&
        !/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(state.domain)
      ) {
        return { complete: false };
      }
      if (state.panelPort) {
        const port = Number(state.panelPort);
        if (!Number.isInteger(port) || port < 1 || port > 65535) return { complete: false };
      }
      return { complete: true };
    }

    case 'review':
      return { complete: true };
  }
}

/** Steps the customer may jump back to, i.e. every earlier completed one. */
export function reachableSteps(
  current: WizardStep,
  state: WizardState,
  context: Parameters<typeof validateStep>[2],
): readonly WizardStep[] {
  const reachable: WizardStep[] = [];
  for (const step of WIZARD_STEPS) {
    reachable.push(step);
    if (step === current) break;
    if (!validateStep(step, state, context).complete) break;
  }
  return reachable;
}
